/**
 * PR-29 — Prediction auto-dismiss policy.
 * - 6s when ignored.
 * - +2s extension when the user interacts (vote / hover / tap).
 * - On expiry the card collapses into a compact 36px reopenable badge.
 */
import { useEffect, useState } from "react";
import { trackEvent } from "@/lib/audit";

export const PREDICTION_IDLE_MS = 6_000;
export const PREDICTION_INTERACTED_MS = 8_000;
export const PREDICTION_BADGE_HEIGHT_PX = 36;

type State = "card" | "minimized" | "closed";

interface Store {
  state: State;
  questionId: string | null;
}

const listeners = new Set<() => void>();
let store: Store = { state: "closed", questionId: null };
function emit() { listeners.forEach((l) => l()); }

export function openPrediction(questionId: string) {
  store = { state: "card", questionId };
  emit();
}
export function minimizePrediction(reason: "timeout" | "manual" = "timeout") {
  if (store.state !== "card") return;
  store = { ...store, state: "minimized" };
  emit();
  void trackEvent("prediction_auto_minimized", { question_id: store.questionId, reason });
}
export function reopenPrediction() {
  if (store.state !== "minimized") return;
  store = { ...store, state: "card" };
  emit();
  void trackEvent("prediction_reopened", { question_id: store.questionId });
}
export function closePrediction() {
  store = { state: "closed", questionId: null };
  emit();
}

export function usePredictionAutoDismiss(): Store {
  const [s, setS] = useState(store);
  useEffect(() => {
    const fn = () => setS(store);
    listeners.add(fn);
    return () => { listeners.delete(fn); };
  }, []);
  return s;
}